import React, { useState, useEffect, useRef } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../components/ui/select';
import { Input } from '../../../../components/ui/input';
import { Button } from '../../../../components/ui/button';
import {
  RadioGroup,
  RadioGroupItem,
} from '../../../../components/ui/radio-group';
import { Label } from '../../../../components/ui/label';
import socialMediaService from 'features/social-media/services/socialMediaCommonApi';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Skeleton } from 'components/ui/skeleton';
import { UploadIcon, Sparkle, Loader } from 'lucide-react';
import { GenerateSocialMediaRequest } from 'features/social-media/types';
import { useSocialMediaContext } from 'features/social-media/SocialMediaContext';
import { useNavigate } from 'react-router-dom';

interface PostEditPanelProps {
  onClose?: () => void;
  onGenerate?: (formData: GenerateSocialMediaRequest) => void;
  formOptions?: any;
}

const PostEditPanel = ({ formOptions }: PostEditPanelProps) => {
  const [fileName, setFileName] = useState<string | null>(null);
  const [files, setFiles] = useState<File | null>();

  const { updateFormData, handleUpdateSocialMediaContent } =
    useSocialMediaContext();

  const paramsId = new URLSearchParams(window.location.search).get('name');

  const abortControllerRef = useRef<AbortController | null>(null);

  const mutations = useRef<any[]>([]);

  const navigate = useNavigate();

  const topics = useQuery({
    queryKey: ['fetch-meta-topics'],
    queryFn: socialMediaService.getMetaTopics,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  const generatePost = useMutation({
    mutationKey: ['generate-social-media-post'],
    mutationFn: (data: GenerateSocialMediaRequest) =>
      socialMediaService.generatePost(data, abortControllerRef.current?.signal),
    onSuccess: ({ data }) => {
      handleUpdateSocialMediaContent(data);
      updateFormData({
        description: formData.description,
        drafts: formData.numDrafts,
        imageType: formData.imageType,
        language: formData.language,
        subTopic: formData.post_subtopic,
        tone: formData.toneOfVoice,
        topic: formData.post_topic,
      });

      navigate('/social-media/response?name=' + paramsId);
    },
  });

  const uploadUserImage = useMutation({
    mutationKey: ['upload-user-image'],
    mutationFn: socialMediaService.uploadImage,
  });

  const [formData, setFormData] = useState({
    description: '',
    toneOfVoice: '',
    language: '',
    numDrafts: 1,
    imageType: 'generate', // 'generate' or 'upload'
    post_topic: '',
    post_subtopic: '',
  });

  const [FilterFormOptions, setFilterFormOptions] = useState({
    topic: [] as string[],
    sub_topics: [] as string[],
    tones: [] as string[],
    languages: [] as string[],
  });

  const handleInputChange = (field: string, value: string | number) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    setFiles(file);
    setFileName(file ? file.name : null);
  };

  const handleGeneratePost = async () => {
    mutations.current.push(
      generatePost.mutateAsync({
        query: formData.description,
        img_required: formData.imageType === 'upload' ? false : true,
        language: formData.language,
        number_of_posts: formData.numDrafts,
        platform_key:
          paramsId == 'linkedIn' ? 'LinkedInContent' : 'TwitterContent',
        number_of_words: 250,
        post_subtopic: formData.post_subtopic,
        post_topic: formData.post_topic,
        tone_key: formData.toneOfVoice,
      })
    );

    if (formData.imageType == 'upload') {
      const formData = new FormData();

      if (files) {
        formData.append('file', files);
      }
      mutations.current.push(
        uploadUserImage.mutateAsync({
          file: formData,
        })
      );
    }

    try {
      const results = await Promise.all(mutations.current);
      console.log('All mutations completed:', results);
    } catch (error) {
      console.error('One or more mutations failed:', error);
    }

    // onGenerate?.(formData);
  };

  // Initialize abort controller on mount and clean up on unmount
  useEffect(() => {
    abortControllerRef.current = new AbortController();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Add beforeunload event listener to abort requests on page refresh/exit
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    if (formOptions?.data) {
      const platformKey = paramsId === 'linkedin' ? 'LinkedInContent' : 'TwitterContent';
      setFilterFormOptions({
        topic: [formOptions.data.platform_topics[platformKey].topic],
        sub_topics: formOptions.data.platform_topics[platformKey].sub_topics,
        tones: formOptions.data.tones,
        languages: formOptions.data.languages,
      });
    }
  }, [formOptions, paramsId]);

  const isFormValid = () => {
    return (
      formData.description &&
      formData.toneOfVoice &&
      formData.language &&
      formData.post_topic &&
      formData.post_subtopic
    );
  };

  return (
    <div className="w-full lg:max-w-[400px] h-full overflow-y-auto bg-background text-text-primary">
      <div className="lg:p-6 space-y-6">
        {/* Topic */}
        <div className="space-y-2">
          <label className="text-sm font-medium">Topic</label>
          {topics.isLoading ? (
            <Skeleton className="h-10" />
          ) : (
            <Select
              onValueChange={(value) => handleInputChange('post_topic', value)}
            >
              <SelectTrigger className="w-full bg-background border">
                <SelectValue placeholder="Select Topic" />
              </SelectTrigger>
              <SelectContent className="max-h-[200px]">
                {topics.isSuccess &&
                  topics.data.data
                    .filter((i) => {
                      const query = paramsId === 'linkedIn' ? 'LinkedInContent' : 'TwitterContent';
                      return i.platform_name === query;
                    })
                    .map((opt) => (
                      <SelectItem key={opt.id} value={opt.topic}>
                        {opt.topic}
                      </SelectItem>
                    ))}
              </SelectContent>
            </Select>
          )}
        </div>

        {/* Sub-topic */}
        <div className="space-y-2">
          <label className="text-sm font-medium">Sub-topic</label>
          {topics.isLoading ? (
            <Skeleton className="h-10" />
          ) : (
            <Select
              onValueChange={(value) => handleInputChange('post_subtopic', value)}
              disabled={formData.post_topic === ''}
            >
              <SelectTrigger className="w-full bg-background border">
                <SelectValue placeholder="Select Subtopic" />
              </SelectTrigger>
              <SelectContent className="max-h-[200px]">
                {topics.isSuccess &&
                  formData.post_topic !== '' &&
                  topics.data.data
                    .find((i) => i.topic === formData.post_topic)
                    ?.sub_topics.map((t, index) => (
                      <SelectItem key={index} value={t}>
                        {t}
                      </SelectItem>
                    ))}
              </SelectContent>
            </Select>
          )}
        </div>

        {/* Description */}
        <div className="space-y-2">
          <label className="text-sm font-medium">Description</label>
          <textarea
            className="w-full min-h-[100px] px-3 py-2 border bg-background rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="Enter description here"
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
          />
        </div>

        {/* Language and Drafts in same row */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Language</label>
            <Select
              value={formData.language}
              onValueChange={(value) => handleInputChange('language', value)}
            >
              <SelectTrigger className="w-full bg-background border">
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                {FilterFormOptions.languages.map((lang, index) => (
                  <SelectItem key={index} value={lang}>
                    {lang}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">No. of Drafts</label>
            <Select
              value={formData.numDrafts.toString()}
              onValueChange={(value) => handleInputChange('numDrafts', parseInt(value))}
            >
              <SelectTrigger className="w-full bg-background border">
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                {[1, 2, 3, 4, 5].map((num) => (
                  <SelectItem key={num} value={num.toString()}>
                    {num}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Tone of voice */}
        <div className="space-y-2">
          <label className="text-sm font-medium">Tone of voice</label>
          <Select
            value={formData.toneOfVoice}
            onValueChange={(value) => handleInputChange('toneOfVoice', value)}
          >
            <SelectTrigger className="w-full bg-background border">
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent className="max-h-[200px]">
              {FilterFormOptions.tones.map((option: string, index: number) => (
                <SelectItem key={index} value={option}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Image Type */}
        {/* <div className="space-y-2">
          <label className="text-sm font-medium">Image Type</label>
          <RadioGroup
            value={formData.imageType}
            onValueChange={(value) => handleInputChange('imageType', value)}
            className="flex flex-col gap-2"
          >
            <div className="flex items-center gap-2">
              <RadioGroupItem value="generate" id="generate" className="text-primary" />
              <Label htmlFor="generate">Generate Image</Label>
            </div>
            <div className="flex items-center gap-2">
              <RadioGroupItem value="upload" id="upload" className="text-primary" />
              <Label htmlFor="upload">Upload Image</Label>
            </div>
          </RadioGroup>
        </div> */}

        {/* Image upload */}
        {formData.imageType === 'upload' && (
          <div className="flex flex-col relative items-center">
            <input
              type="file"
              id="image-upload"
              className="sr-only"
              accept="image/*"
              onChange={handleFileChange}
            />
            <label
              htmlFor="image-upload"
              className="py-2 cursor-pointer border border-dashed border-primary rounded-xl text-primary flex items-center justify-center gap-2 w-full font-medium"
            >
              <UploadIcon size={18} />
              Upload
            </label>
            {fileName && (
              <p className="bg-gray-900 p-2 text-balance rounded-md w-full mt-2 text-sm text-white">
                {fileName}
              </p>
            )}
          </div>
        )}

        {/* Generate Button */}
        <Button
          variant="customPrimary"
          disabled={!isFormValid() || generatePost.isPending || uploadUserImage.isPending}
          className="w-full mt-6"
          onClick={handleGeneratePost}
        >
          {generatePost.isPending || uploadUserImage.isPending ? (
            <>
              <Loader className="w-5 h-5 animate-spin mr-2" />
              <span>Generating...</span>
            </>
          ) : (
            <>
              <Sparkle className="w-5 h-5 mr-2" />
              <span>Generate post</span>
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default PostEditPanel;
